import { useState } from 'react'
import { Link } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import api from '../api/client'
import CEFRBadge from '../components/shared/CEFRBadge'
import type { AdminDeck, AdminDeckUpdate, WordDictionarySummary } from '../types'

// ── API helpers ───────────────────────────────────────────────────────────────

async function fetchSystemDecks(): Promise<AdminDeck[]> {
  const { data } = await api.get<AdminDeck[]>('/api/v1/admin/system-decks/')
  return data
}

async function fetchWordDictionary(): Promise<WordDictionarySummary> {
  const { data } = await api.get<WordDictionarySummary>('/api/v1/admin/word-dictionary/')
  return data
}

async function patchDeck(id: string, payload: AdminDeckUpdate): Promise<AdminDeck> {
  const { data } = await api.patch<AdminDeck>(`/api/v1/admin/system-decks/${id}/`, payload)
  return data
}

// ── Sub-components ────────────────────────────────────────────────────────────

function VisibilityToggle({ deck }: { deck: AdminDeck }) {
  const qc = useQueryClient()
  const mutation = useMutation({
    mutationFn: () => patchDeck(deck.id, { is_public: !deck.is_public }),
    onSuccess: (updated) => {
      qc.setQueryData<AdminDeck[]>(['admin-system-decks'], (prev) =>
        prev ? prev.map((d) => (d.id === updated.id ? updated : d)) : prev,
      )
    },
  })

  return (
    <button
      onClick={() => mutation.mutate()}
      disabled={mutation.isPending}
      style={{
        padding: '4px 12px',
        borderRadius: 50,
        border: 'none',
        cursor: mutation.isPending ? 'not-allowed' : 'pointer',
        fontSize: 12,
        fontWeight: 600,
        backgroundColor: deck.is_public ? 'var(--success)' : 'var(--bg-secondary)',
        color: deck.is_public ? '#fff' : 'var(--text-secondary)',
        opacity: mutation.isPending ? 0.6 : 1,
        transition: 'background-color 0.15s',
        minWidth: 72,
      }}
    >
      {deck.is_public ? 'Public' : 'Hidden'}
    </button>
  )
}

interface EditModalProps {
  deck: AdminDeck
  onClose: () => void
}

function EditModal({ deck, onClose }: EditModalProps) {
  const qc = useQueryClient()
  const [title, setTitle] = useState(deck.title)
  const [description, setDescription] = useState(deck.description ?? '')
  const [cefrLevel, setCefrLevel] = useState(deck.cefr_level ?? '')
  const [error, setError] = useState<string | null>(null)

  const mutation = useMutation({
    mutationFn: () =>
      patchDeck(deck.id, {
        title: title.trim() || undefined,
        description: description.trim() || undefined,
        cefr_level: cefrLevel.trim() || undefined,
      }),
    onSuccess: (updated) => {
      qc.setQueryData<AdminDeck[]>(['admin-system-decks'], (prev) =>
        prev ? prev.map((d) => (d.id === updated.id ? updated : d)) : prev,
      )
      onClose()
    },
    onError: () => setError('Failed to save. Try again.'),
  })

  return (
    <div
      style={{
        position: 'fixed', inset: 0, zIndex: 100,
        backgroundColor: 'rgba(0,0,0,0.5)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose() }}
    >
      <div style={{
        backgroundColor: 'var(--bg-surface)',
        borderRadius: 16,
        padding: 32,
        width: 480,
        maxWidth: '90vw',
        boxShadow: 'var(--shadow-float)',
      }}>
        <h2 style={{ margin: '0 0 20px', fontSize: 18, color: 'var(--text-primary)' }}>
          Edit Deck
        </h2>

        <label style={labelStyle}>Title</label>
        <input
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          style={inputStyle}
        />

        <label style={labelStyle}>Description</label>
        <textarea
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          rows={3}
          style={{ ...inputStyle, resize: 'vertical' }}
        />

        <label style={labelStyle}>CEFR Level</label>
        <select
          value={cefrLevel}
          onChange={(e) => setCefrLevel(e.target.value)}
          style={inputStyle}
        >
          <option value="">— none —</option>
          {['A1', 'A2', 'B1', 'B2', 'C1', 'C2'].map((l) => (
            <option key={l} value={l}>{l}</option>
          ))}
        </select>

        {error && (
          <p style={{ color: 'var(--danger)', fontSize: 13, margin: '8px 0 0' }}>{error}</p>
        )}

        <div style={{ display: 'flex', gap: 12, marginTop: 24, justifyContent: 'flex-end' }}>
          <button onClick={onClose} style={secondaryBtnStyle}>Cancel</button>
          <button
            onClick={() => mutation.mutate()}
            disabled={mutation.isPending}
            style={{ ...primaryBtnStyle, opacity: mutation.isPending ? 0.7 : 1 }}
          >
            {mutation.isPending ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  )
}

// ── Main page ─────────────────────────────────────────────────────────────────

export default function AdminPage() {
  const [editingDeck, setEditingDeck] = useState<AdminDeck | null>(null)

  const decksQuery = useQuery({
    queryKey: ['admin-system-decks'],
    queryFn: fetchSystemDecks,
  })

  const dictQuery = useQuery({
    queryKey: ['admin-word-dictionary'],
    queryFn: fetchWordDictionary,
  })

  // Group decks by language for display
  const byLanguage = (decksQuery.data ?? []).reduce<Record<string, AdminDeck[]>>(
    (acc, deck) => {
      ;(acc[deck.language] = acc[deck.language] ?? []).push(deck)
      return acc
    },
    {},
  )

  return (
    <div style={{ maxWidth: 1000 }}>
      <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', marginBottom: 8 }}>
        <h1 style={{ fontSize: 24, fontWeight: 700, color: 'var(--text-primary)', margin: 0 }}>
          Admin Panel
        </h1>
        <div style={{ display: 'flex', gap: 20 }}>
          <Link
            to="/admin/activity"
            style={{ fontSize: 13, fontWeight: 600, color: 'var(--accent)', textDecoration: 'none' }}
          >
            Tester Activity →
          </Link>
          <Link
            to="/admin/dashboard"
            style={{ fontSize: 13, fontWeight: 600, color: 'var(--accent)', textDecoration: 'none' }}
          >
            Weekly Dashboard →
          </Link>
        </div>
      </div>
      <p style={{ color: 'var(--text-secondary)', fontSize: 14, margin: '0 0 40px' }}>
        Manage system-owned public decks and monitor the word dictionary.
      </p>

      {/* ── System Decks ────────────────────────────────────────────────── */}
      <section style={{ marginBottom: 56 }}>
        <h2 style={sectionHeadingStyle}>System Decks</h2>

        {decksQuery.isLoading && (
          <p style={{ color: 'var(--text-secondary)', fontSize: 14 }}>Loading…</p>
        )}
        {decksQuery.isError && (
          <p style={{ color: 'var(--danger)', fontSize: 14 }}>Failed to load decks.</p>
        )}

        {Object.entries(byLanguage).sort(([a], [b]) => a.localeCompare(b)).map(([lang, decks]) => (
          <div key={lang} style={{ marginBottom: 32 }}>
            <h3 style={{
              fontSize: 13,
              fontWeight: 700,
              textTransform: 'uppercase',
              letterSpacing: '0.08em',
              color: 'var(--text-secondary)',
              margin: '0 0 12px',
            }}>
              {lang.toUpperCase()}
            </h3>

            <div style={{
              backgroundColor: 'var(--bg-surface)',
              borderRadius: 12,
              border: '1px solid var(--border)',
              overflow: 'hidden',
            }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ borderBottom: '1px solid var(--border)' }}>
                    {['Title', 'Level', 'Cards', 'Visibility', 'Translations', 'Actions'].map((h) => (
                      <th
                        key={h}
                        style={{
                          padding: '10px 16px',
                          textAlign: 'left',
                          fontSize: 11,
                          fontWeight: 700,
                          textTransform: 'uppercase',
                          letterSpacing: '0.06em',
                          color: 'var(--text-secondary)',
                        }}
                      >
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {decks.map((deck, i) => (
                    <tr
                      key={deck.id}
                      style={{
                        borderBottom: i < decks.length - 1 ? '1px solid var(--border)' : 'none',
                      }}
                    >
                      <td style={cellStyle}>
                        <span style={{ fontWeight: 500, color: 'var(--text-primary)', fontSize: 14 }}>
                          {deck.title}
                        </span>
                        {deck.description && (
                          <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 2 }}>
                            {deck.description.length > 60
                              ? deck.description.slice(0, 60) + '…'
                              : deck.description}
                          </div>
                        )}
                      </td>
                      <td style={cellStyle}>
                        {deck.cefr_level
                          ? <CEFRBadge level={deck.cefr_level} size="sm" />
                          : <span style={{ color: 'var(--text-secondary)', fontSize: 13 }}>—</span>
                        }
                      </td>
                      <td style={{ ...cellStyle, color: 'var(--text-secondary)', fontSize: 14 }}>
                        {deck.card_count}
                      </td>
                      <td style={cellStyle}>
                        <VisibilityToggle deck={deck} />
                      </td>
                      <td style={cellStyle}>
                        {deck.translation_languages.length > 0 ? (
                          <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                            {deck.translation_languages.map((lang) => (
                              <span
                                key={lang}
                                style={{
                                  fontSize: 11,
                                  fontWeight: 600,
                                  padding: '2px 6px',
                                  borderRadius: 4,
                                  backgroundColor: 'var(--bg-secondary)',
                                  color: 'var(--text-secondary)',
                                }}
                              >
                                {lang}
                              </span>
                            ))}
                          </div>
                        ) : (
                          <span style={{ color: 'var(--danger)', fontSize: 12 }}>None</span>
                        )}
                      </td>
                      <td style={cellStyle}>
                        <button
                          onClick={() => setEditingDeck(deck)}
                          style={actionBtnStyle}
                        >
                          Edit
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        ))}

        {decksQuery.isSuccess && decksQuery.data.length === 0 && (
          <p style={{ color: 'var(--text-secondary)', fontSize: 14 }}>
            No system decks found. Run the seed script to populate them.
          </p>
        )}
      </section>

      {/* ── Word Dictionary Summary ──────────────────────────────────────── */}
      <section>
        <h2 style={sectionHeadingStyle}>Word Dictionary</h2>

        {dictQuery.isLoading && (
          <p style={{ color: 'var(--text-secondary)', fontSize: 14 }}>Loading…</p>
        )}
        {dictQuery.isError && (
          <p style={{ color: 'var(--danger)', fontSize: 14 }}>Failed to load dictionary stats.</p>
        )}

        {dictQuery.data?.languages.map((lang) => (
          <div
            key={lang.language}
            style={{
              backgroundColor: 'var(--bg-surface)',
              border: '1px solid var(--border)',
              borderRadius: 12,
              padding: 20,
              marginBottom: 16,
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12 }}>
              <span style={{
                fontSize: 15,
                fontWeight: 700,
                color: 'var(--text-primary)',
              }}>
                {lang.language.toUpperCase()}
              </span>
              <span style={{
                fontSize: 13,
                color: 'var(--text-secondary)',
              }}>
                {lang.total_entries.toLocaleString()} entries
              </span>
            </div>

            {lang.translations.length === 0 ? (
              <p style={{ margin: 0, fontSize: 13, color: 'var(--danger)' }}>
                No translations seeded yet.
              </p>
            ) : (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                {lang.translations.map((t) => {
                  const pct = Math.round((t.count / lang.total_entries) * 100)
                  const isMissing = t.missing > 0
                  return (
                    <div
                      key={t.source_language}
                      style={{
                        padding: '8px 14px',
                        borderRadius: 8,
                        backgroundColor: 'var(--bg-secondary)',
                        minWidth: 120,
                      }}
                    >
                      <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-primary)', marginBottom: 4 }}>
                        → {t.source_language.toUpperCase()}
                      </div>
                      <div style={{ fontSize: 12, color: 'var(--text-secondary)' }}>
                        {t.count.toLocaleString()} / {lang.total_entries.toLocaleString()} ({pct}%)
                      </div>
                      {isMissing && (
                        <div style={{ fontSize: 11, color: 'var(--warning)', marginTop: 2 }}>
                          {t.missing.toLocaleString()} missing
                        </div>
                      )}
                    </div>
                  )
                })}
              </div>
            )}
          </div>
        ))}

        {dictQuery.isSuccess && dictQuery.data.languages.length === 0 && (
          <p style={{ color: 'var(--text-secondary)', fontSize: 14 }}>
            No word entries found. Run the seed script to populate the dictionary.
          </p>
        )}
      </section>

      {/* ── Modals ──────────────────────────────────────────────────────── */}
      {editingDeck && (
        <EditModal deck={editingDeck} onClose={() => setEditingDeck(null)} />
      )}
    </div>
  )
}

// ── Style constants ───────────────────────────────────────────────────────────

const sectionHeadingStyle: React.CSSProperties = {
  fontSize: 16,
  fontWeight: 700,
  color: 'var(--text-primary)',
  margin: '0 0 20px',
  paddingBottom: 12,
  borderBottom: '1px solid var(--border)',
}

const cellStyle: React.CSSProperties = {
  padding: '12px 16px',
  verticalAlign: 'middle',
}

const actionBtnStyle: React.CSSProperties = {
  background: 'none',
  border: 'none',
  cursor: 'pointer',
  fontSize: 13,
  fontWeight: 600,
  color: 'var(--accent)',
  padding: '4px 2px',
}

const labelStyle: React.CSSProperties = {
  display: 'block',
  fontSize: 12,
  fontWeight: 600,
  color: 'var(--text-secondary)',
  marginBottom: 6,
  marginTop: 16,
}

const inputStyle: React.CSSProperties = {
  display: 'block',
  width: '100%',
  padding: '8px 12px',
  borderRadius: 8,
  border: '1px solid var(--border)',
  backgroundColor: 'var(--bg-secondary)',
  color: 'var(--text-primary)',
  fontSize: 14,
  boxSizing: 'border-box',
}

const primaryBtnStyle: React.CSSProperties = {
  padding: '8px 20px',
  borderRadius: 50,
  border: 'none',
  cursor: 'pointer',
  fontSize: 14,
  fontWeight: 600,
  backgroundColor: 'var(--accent-fill)',
  color: 'var(--text-on-accent)',
}

const secondaryBtnStyle: React.CSSProperties = {
  padding: '8px 20px',
  borderRadius: 50,
  border: '1px solid var(--border)',
  cursor: 'pointer',
  fontSize: 14,
  fontWeight: 600,
  backgroundColor: 'transparent',
  color: 'var(--text-secondary)',
}
